import { useCallback } from "react";
import styles from "./SupportingDocumentsSubmittedModalPage.module.css";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { Gifs } from "@/assets/gifs";
import { Modal } from "@/shared/modal";
import { Card } from "@/shared/components/Card";
import { PowerBillCard } from "@/features/powerBill/views/powerBillCard";
import { useAddress } from "@/shared/hooks";
import { ArrowRightIcon } from "@/assets/svg/ArrowRightIcon";
import homeIcon from "@/assets/icons1/home.png";

type SupportingDocumentsSubmittedModalPageProps = {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  propertyType?: "RESIDENTIAL" | "COMMERCIAL" | null;
};

const HomesteadHouseIcon = () => (
  <svg viewBox="0 0 24 24" width={22} height={22} fill="none" aria-hidden="true">
    <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-8.5Z" fill="#FFFFFF" />
  </svg>
);

export const SupportingDocumentsSubmittedModalPage = ({
  isOpen,
  onClose,
  onContinue,
  propertyType = null,
}: SupportingDocumentsSubmittedModalPageProps) => {
  const navigate = useNavigate();
  const { selectedAddress } = useAddress();

  const isResidential = propertyType === "RESIDENTIAL";
  const protestTypeLabel = isResidential ? "Residential" : "Commercial";

  const handleHomesteadApply = useCallback(() => {
    onClose();
    onContinue();
    navigate(routes.homesteadExemption);
  }, [navigate, onClose, onContinue]);

  const handleContinue = useCallback(() => {
    onClose();
    onContinue();
    navigate(routes.homepage);
  }, [navigate, onClose, onContinue]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      closeOnBackdropClick={false}
      unconstrained
      className={styles.modal}
      bodyClassName={styles.submittedModalBody}
      ariaLabel={`${protestTypeLabel} protest documents submitted`}
    >
      <h2 className={styles.successTitle}>{protestTypeLabel} Protest Documents Submitted!</h2>
      <img src={Gifs.andyConfetti} alt="Confetti celebration" className={styles.confettiGif} />
      <p className={styles.successSubtitle}>
        Your {protestTypeLabel.toLowerCase()} supporting documents have been successfully submitted
        with supporting document. We&apos;ll keep you updated on its progress!
      </p>

      {isResidential && (
        <>
          <div className={styles.homesteadCardWrap}>
            <Card className={styles.homesteadCard}>
              <div className={styles.homesteadTop}>
                <div className={styles.homesteadLeft}>
                  <div className={styles.homesteadIcon}>
                    <HomesteadHouseIcon />
                  </div>

                  <div className={styles.homesteadText}>
                    <div className={styles.homesteadTitle}>File a Homestead Exemption</div>
                    <div className={styles.homesteadDescription}>
                      File a homestead exemption to save up to 20% on your property taxes.
                    </div>
                  </div>
                </div>

                <img src={homeIcon} className={styles.homesteadIllustration} alt="" />
              </div>

              <button
                type="button"
                className={styles.homesteadFooter}
                onClick={handleHomesteadApply}
              >
                <span>Apply Now</span>
                <div className={styles.smallArrow}>
                  <ArrowRightIcon width={16} height={16} />
                </div>
              </button>
            </Card>
          </div>

          <div className={styles.powerBillCardWrap}>
            <PowerBillCard
              navigateTo={routes.powerCardPlans}
              selectedProperty={selectedAddress || ""}
            />
          </div>
        </>
      )}

      <div className={styles.successActions}>
        <button type="button" className={styles.continueBtn} onClick={handleContinue}>
          Continue
        </button>
      </div>
    </Modal>
  );
};
